import { cn } from '@sohanemon/utils';
import { Iconify, Portal } from '@sohanemon/utils/components';
import { m } from '@sohanscript/i18n';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@sohanscript/ui/dropdown-menu';
import { Motion } from '@sohanscript/ui/motion';
import { Separator } from '@sohanscript/ui/separator';
import type { ComponentProps } from 'react';
import { Link } from '@/components/link';

type PageBreadcrumbProps = ComponentProps<'div'> & {
	currentSlug: string;
	currentTitle?: string;
	listTo: string;
	listLabel: string;
	itemTo: string;
	items: Array<{ slug: string; title: string }>;
};

export function PageBreadcrumb({
	currentSlug,
	currentTitle,
	listTo,
	listLabel,
	itemTo,
	items,
	className,
	...props
}: PageBreadcrumbProps) {
	return (
		<Portal container="#navbar-content">
			<Separator className="opacity-40 max-md:hidden" />
			<div className={cn('container py-2 max-md:hidden', className)} {...props}>
				<nav aria-label={m.bc_breadcrumb_aria()} className="flex">
					<ol className="inline-flex items-center space-x-1 md:space-x-3 rtl:space-x-reverse rtl:md:space-x-reverse">
						<Motion className="inline-flex items-center" initial="hidden">
							<Link
								className="inline-flex items-center font-medium text-sm hover:text-primary"
								to="/"
							>
								<Iconify
									className="mr-2.5 rtl:mr-0 rtl:ml-2.5"
									icon="ic:round-home"
								/>
								{m.bc_home()}
							</Link>
						</Motion>
						<Motion
							className="flex items-center"
							initial="hidden"
							transition={{ delay: 0.3 }}
						>
							<Iconify
								className="mx-1 rtl:-scale-x-100"
								icon="mingcute:right-line"
							/>
							<Link
								className="ms-1 font-medium text-sm hover:text-primary md:ms-2"
								to={listTo as '/'}
							>
								{listLabel}
							</Link>
						</Motion>
						<Motion
							className="flex items-center"
							initial="hidden"
							transition={{ delay: 0.6 }}
						>
							<Iconify
								className="mx-1 rtl:-scale-x-100"
								icon="mingcute:right-line"
							/>
							<DropdownMenu>
								<DropdownMenuTrigger className="ms-1 inline-flex items-center gap-1 text-start font-medium text-primary text-sm capitalize hover:text-primary/80 md:ms-2">
									<span className="line-clamp-1 max-w-72">
										{currentTitle ?? currentSlug}
									</span>
									<Iconify icon="lucide:chevron-down" className="size-3.5" />
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									{items.map((item) => (
										<DropdownMenuItem key={item.slug}>
											<Link
												to={itemTo as '/'}
												params={{ slug: item.slug } as never}
												className={cn(
													'line-clamp-1 w-full text-sm',
													item.slug === currentSlug && 'text-primary',
												)}
											>
												{item.title}
											</Link>
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						</Motion>
					</ol>
				</nav>
			</div>
			<Separator className="opacity-40 max-md:hidden" />
		</Portal>
	);
}
